import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getCreditUsage } from '../api/credits.js'

/**
 * Credit-usage store. `usage` holds the raw `/credits/usage` response
 * (pools + window breakdowns). `configured` is `false` when credits are
 * disabled server-side — in which case the UI renders nothing at all.
 *
 * The store drives two surfaces:
 *  - the drawer's Credit Usage panel (caller-driven refresh, no polling), and
 *  - the boot-time **credit gate** (`CreditGate.vue`): when credits are
 *    enforced server-side and the team has no pool with any balance, the widget
 *    replaces chat with a "contact sales" takeover. `gated` / `gateReason`
 *    below encode that decision; `checked` lets the gate distinguish
 *    "still checking" from "checked, not gated".
 *
 * FAIL OPEN: on a `load()` error `gated` stays false so a flaky pre-flight
 * check never locks out users — the server still enforces at turn time.
 */
export const useCreditsStore = defineStore('credits', () => {
  const usage = ref(null)
  const configured = ref(true)
  // Mirrors the response's `enforcing` flag (true only in Enforce mode). The
  // gate requires enforcement: in Monitor mode chat works and spend is only
  // shadow-logged, so we must not gate.
  const enforcing = ref(false)
  const loading = ref(false)
  const error = ref(null)
  const lastFetched = ref(null)
  // True once the first load() has resolved (success OR failure). Lets the gate
  // show a spinner while unknown, then commit to a decision.
  const checked = ref(false)
  // The caller's own validated identity from the usage response
  // ({ displayName, email, teamName, teamCode }). Used to prefill the
  // contact-sales form; null until loaded or when credits are disabled.
  const user = ref(null)

  const pools = computed(() => usage.value?.pools ?? [])
  const hasCredit = computed(() => pools.value.some(p => (p?.balance ?? 0) > 0))
  // The gate engages only when credits are configured AND enforced AND no pool
  // carries a usable balance. On load() error `configured`/`enforcing` are left
  // untouched-or-false, so this stays false (fail open).
  const gated = computed(() => configured.value === true && enforcing.value === true && !hasCredit.value)
  // `no_pool` (never provisioned → sales pitch) vs `exhausted` (had pools, all
  // depleted → renewal). Only meaningful when `gated`.
  const gateReason = computed(() => (pools.value.length ? 'exhausted' : 'no_pool'))

  async function load() {
    loading.value = true
    error.value = null
    try {
      const data = await getCreditUsage()
      if (data?.configured === false) {
        configured.value = false
        enforcing.value = false
        usage.value = null
        user.value = null
      } else {
        configured.value = true
        enforcing.value = data?.enforcing === true
        usage.value = data
        user.value = data?.user ?? null
      }
      lastFetched.value = Date.now()
    } catch (e) {
      error.value = e.message
      // Fail open: leave `gated` false. Do not flip `configured`/`enforcing`.
    } finally {
      checked.value = true
      loading.value = false
    }
  }

  return {
    usage, configured, enforcing, loading, error, lastFetched, checked, user,
    pools, hasCredit, gated, gateReason,
    load,
  }
})
